import { FUNDING_FEE_TYPE_PCT } from './buildFundingFeeSection';
import { resolveFundingCommitmentMonths } from '../QuickCashflowModel/calc/resolveFundingCommitment';
import { resolveMonthlyFeePct } from '../QuickCashflowModel/calc/xirrFee';

export const FIELD_SOURCE_QUICK_CASHFLOW = 'quickCashflow';

function parseQuickCashflowModelJson(modelJson) {
  if (!modelJson) {
    return null;
  }
  if (typeof modelJson === 'string') {
    try {
      return JSON.parse(modelJson);
    } catch {
      return null;
    }
  }
  return modelJson;
}

function isFilledNumber(value) {
  if (value === '' || value == null) {
    return false;
  }
  return Number.isFinite(Number(value));
}

function toDecimalPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) {
    return null;
  }
  return n > 1 ? n / 100 : n;
}

function setNumber(fields, fieldSources, key, value) {
  if (!isFilledNumber(value)) {
    return;
  }
  const n = Number(value);
  if (n <= 0) {
    return;
  }
  fields[key] = n;
  fieldSources[key] = { source: FIELD_SOURCE_QUICK_CASHFLOW };
}

function setPct(fields, fieldSources, key, humanPct) {
  const decimal = toDecimalPct(humanPct);
  if (decimal == null) {
    return;
  }
  fields[key] = decimal;
  fieldSources[key] = { source: FIELD_SOURCE_QUICK_CASHFLOW };
}

function resolveFundingPct(model) {
  if (isFilledNumber(model.defaultFundingPct) && Number(model.defaultFundingPct) > 0) {
    return Number(model.defaultFundingPct);
  }
  const fromPlan = (model.fundingPct ?? []).find((pct) => Number(pct) > 0);
  return fromPlan == null ? null : Number(fromPlan);
}

function resolveCohortCount(model) {
  const fromPlan = resolveFundingCommitmentMonths(model);
  if (fromPlan > 0) {
    return fromPlan;
  }
  if (isFilledNumber(model.commitmentMonths) && Number(model.commitmentMonths) > 0) {
    return Math.floor(Number(model.commitmentMonths));
  }
  return null;
}

/**
 * Map First Underwriting / Quick Cashflow model JSON into term sheet fields.
 * Only filled, mappable values are returned; other term sheet fields stay unchanged.
 */
export function buildTermSheetFieldsFromQuickCashflowModel(modelJson) {
  const model = parseQuickCashflowModelJson(modelJson);
  if (!model) {
    return {
      fields: {},
      fieldSources: {},
    };
  }

  const fields = {};
  const fieldSources = {};

  setNumber(fields, fieldSources, 'maxOutstandingInvestment', model.facilitySize);
  setNumber(fields, fieldSources, 'maxAmountPerCohort', model.maxMonthlyDraw);

  const cohortCount = resolveCohortCount(model);
  if (cohortCount != null) {
    setNumber(fields, fieldSources, 'monthlyCohorts', cohortCount);
    setNumber(fields, fieldSources, 'termCohorts', cohortCount);
    setNumber(fields, fieldSources, 'termMonths', cohortCount);
  }

  setPct(fields, fieldSources, 'pctOfFunding', resolveFundingPct(model));
  setPct(fields, fieldSources, 'maxPctIncreaseFromLast', model.maxDrawIncreasePct);

  if (isFilledNumber(model.facilityFeePct) && Number(model.facilityFeePct) > 0) {
    setPct(fields, fieldSources, 'fundingFeePct', model.facilityFeePct);
    fields.fundingFeeType = FUNDING_FEE_TYPE_PCT;
    fieldSources.fundingFeeType = { source: FIELD_SOURCE_QUICK_CASHFLOW };
    if (isFilledNumber(model.facilityFeeInstallments) && Number(model.facilityFeeInstallments) > 0) {
      fields.fundingFeeInstallments = Number(model.facilityFeeInstallments);
      fieldSources.fundingFeeInstallments = { source: FIELD_SOURCE_QUICK_CASHFLOW };
    }
  }

  const monthlyFeePct = resolveMonthlyFeePct(model.feeType, model.feePct);
  if (Number.isFinite(monthlyFeePct) && monthlyFeePct > 0) {
    fields.cappedReturnIrr = monthlyFeePct / 100;
    fieldSources.cappedReturnIrr = { source: FIELD_SOURCE_QUICK_CASHFLOW };
  }

  return { fields, fieldSources };
}

export function applyQuickCashflowPrefillToPayload(
  payload,
  { modelId, modelName, modelJson, prefilledAt = new Date().toISOString() }
) {
  const prefilled = buildTermSheetFieldsFromQuickCashflowModel(modelJson);
  return {
    ...payload,
    fields: {
      ...payload.fields,
      ...prefilled.fields,
    },
    fieldSources: {
      ...payload.fieldSources,
      ...prefilled.fieldSources,
    },
    sources: {
      ...payload.sources,
      quickCashflowModelId: modelId,
      quickCashflowModelName: modelName ?? null,
      prefilledAt,
    },
  };
}

export function formatQuickCashflowModelOptionLabel(model) {
  const value = model?.createdAt || model?.updatedAt;
  const name = model?.name || 'Untitled model';
  if (!value) {
    return name;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return name;
  }
  const savedOn = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  return `${name} (${savedOn})`;
}
